import { useAuth0 } from '@auth0/auth0-react';
import { type Brand, BrandStatus } from '@innovationdepartment/proxima-sdk-axios';
import { type TableConfig, useToaster } from '@innovationdepartment/proxima-ui';
import { useBrandSearchApi, useProximaSDK, useShowSpinner } from 'hooks';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useBrandStore } from 'stores';
import type { BrandLockAudienceOption } from 'types/audiences';
import {
  type AdAccountActiveProps,
  type BrandListIntegrations,
  type BrandListProps,
  type BrandTab,
  PlanFilterOptions
} from 'types/brandList';
import type { AdAccountBatch } from 'types/hooks/useAdManager';
import { type Integration, IntegrationType } from 'types/integrations';
import DeleteConfirmation from './Actions/DeleteConfirmation.Container';
import JoinConfirmation from './Actions/JoinConfirmation/JoinConfirmation.Container';
import LockConfirmation from './Actions/LockConfirmation/LockAccess.Container';
import UnlockConfirmation from './Actions/UnlockConfirmation/UnlockConfirmation.Container';
import BrandListView from './BrandList.View';
import brandRowRender, { type BrandRow } from './brandRowRender';
import CreateBrandModal from './CreateBrandModal';

const MAX_CHARS_FOR_SEARCH = 2;

const debouncer = debounce(cb => cb(), 1000);

// TODO(Jenky): This should be moved inside the component
const brandListConfig: TableConfig<Brand> = {
  // rows reaching the renderer are formatter-enriched BrandRows; TableConfig
  // can't express the raw-row → formatted-row transform, hence the cast
  cellRenderer: args => brandRowRender({ ...args, row: args.row as BrandRow }),
  columns: {
    order: [
      'brandId',
      'name',
      'managed',
      'plan',
      'dailySpend',
      'lastSpend',
      'integration',
      'action'
    ], // TODO(Jenky): add lastUpdatedAt when API is updated
    brandId: { label: 'Brand ID', width: 150 },
    name: { label: 'Brand Name' },
    managed: { label: 'Managed' },
    plan: { label: 'Plan' },
    dailySpend: { label: 'Daily Proxima spend' },
    lastSpend: { label: 'Last Proxima spend' },
    integration: { label: 'Integration' },
    action: {
      label: 'Actions',
      align: 'right',
      hideLabel: true,
      width: 50
    },
    lastUpdateAt: { label: 'Date Joined', width: 240 }
  }
};

const tabs: BrandListProps<Brand>['tabs'] = [
  { label: 'All brands', key: 'all' },
  { label: 'My brands', key: 'favorites' }
];

/** TODO(Jenky): Break down this component into smaller components... this is hard to go around with new logic */
const BrandList = () => {
  const brandTableRef = useRef<HTMLDivElement>(null);
  const {
    loading: brandsLoading,
    getNextBrandBatch,
    getBrands,
    getPreviousBrandBatch,
    hasNext,
    hasPrevious
  } = useBrandSearchApi();
  const { user } = useAuth0();
  const userId = user?.sub;
  const [searchDirty, setSearchDirty] = useState(true);
  const [loading, setLoading] = useState(false);
  const [brandStatusUpdating, setBrandStatusUpdating] = useState(false);
  const userFavoriteApi = useProximaSDK('UsersApi');
  const brandsApi = useProximaSDK('BrandsApi');
  const integrationsApi = useProximaSDK('IntegrationsApi');
  const fbIntegrationsApi = useProximaSDK('FbIntegrationApi');
  const tiktokIntegrationsApi = useProximaSDK('TiktokIntegrationApi');

  const { brand } = useBrandStore();
  // temporary solution to get integrations until a better solution is thought of (Jenky)
  const [integrations, setIntegrations] = useState<BrandListIntegrations>({
    all: [],
    favorites: []
  });
  const { showToaster } = useToaster();
  const [brandIdToDelete, setBrandIdToDelete] = useState('');
  const [brandIdToLock, setBrandIdToLock] = useState('');
  const [brandIdToUnlock, setBrandIdToUnlock] = useState('');
  const [brandIdToJoin, setBrandIdToJoin] = useState('');
  const [filterBrandName, setFilterBrandName] = useState<string>('');
  const [filteredPlans, setFilteredPlans] = useState<string[]>([]);
  const [filteredSpend, setFilteredSpend] = useState<string[]>([]);
  const [includeLockedBrands, setIncludeLockedBrands] = useState(true);
  const [filterStateCounter, setFiltersStateCounter] = useState<number>(0);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [favorites, setFavorites] = useState<Brand[]>([]);
  const [adAccounts, setAdAccounts] = useState<AdAccountBatch>([]);
  const [favoriteAdAccounts, setFavoriteAdAccounts] = useState<AdAccountBatch>([]);

  const [tab, setTab] = useState<BrandTab>('all');
  const [showCreateBrand, setShowCreateBrand] = useState(false);
  const isMounted = useRef(false);
  const hasSelectedFilters = filteredPlans.length > 0 || filteredSpend.length > 0;

  const findBrandToUpdate = (brandId: string) => {
    const favoritesAndBrands = {
      all: brands,
      favorites
    }[tab];
    const brandToUpdate = favoritesAndBrands.find(b => b.brandId === brandId);
    return brandToUpdate;
  };

  // Resolves a brand regardless of the active tab (used by the join flow,
  // which can be triggered from "All brands" but resolved after a tab change).
  const findBrandInAnyList = (brandId: string) =>
    brands.find(b => b.brandId === brandId) ?? favorites.find(b => b.brandId === brandId);

  /* v fetch brands/favorite methods */
  const fetchIntegrationsAndAdAccountsForBrands = async (
    brandIds: string[],
    integrationTab: BrandTab
  ) => {
    const brandId = brandIds.join(',');
    // fetch integrations for favorites
    const [integrationsResponse, adAccountBatchResponse, tiktokAdAccountBatchResponse] =
      await Promise.all([
        integrationsApi.getIntegrationsHistory({ brandId }),
        fbIntegrationsApi.getAdAccountsBatch({ brandId }),
        tiktokIntegrationsApi.getTiktokAdAccountsBatch({ brandId })
      ]);

    // use callback syntax since state updates are asynchronous
    setIntegrations(prevIntegrations => ({
      ...prevIntegrations,
      [integrationTab]: integrationsResponse.data as Integration[]
    }));

    const fbAdAccountBatch = adAccountBatchResponse.data as AdAccountBatch;
    const ttAdAccountBatch = tiktokAdAccountBatchResponse.data as AdAccountBatch;

    const fbAdAccounts = fbAdAccountBatch.map(fbAdAccount => ({
      ...fbAdAccount,
      type: IntegrationType.Facebook
    }));

    const ttAdAccounts = ttAdAccountBatch.map(ttAdAccount => ({
      ...ttAdAccount,
      type: IntegrationType.TikTok
    }));

    const adAccountBatches: AdAccountBatch = [...fbAdAccounts, ...ttAdAccounts];

    const setAdAccountsForTab = {
      all: setAdAccounts,
      favorites: setFavoriteAdAccounts
    };

    const setter = setAdAccountsForTab[integrationTab];
    setter(adAccountBatches);
  };

  const fetchFavorites = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const {
        data: { brandId: brandIds }
      } = await userFavoriteApi.getFavorites({ userId });

      const brandId = brandIds.join(',');

      // fetch integrations for favorites
      const [brandsResponse] = await Promise.all([
        brandsApi.getBrandBatch({ brandId }),
        fetchIntegrationsAndAdAccountsForBrands(brandIds, 'favorites')
      ]);

      setFavorites(brandsResponse.data as Brand[]);
    } catch {
      // TODO: handle error
    }
    setLoading(false);
  };

  // maps brands attributes to a neat object
  const processBrands = (brandsToProcess: Brand[]) => {
    const mappedBrands = brandsToProcess.map((brandData: Brand) => ({
      ...brandData, // @TODO: (Before merge to develop) Felix - For now return all data. After brand index is tested we can remove and purposely select plan and dailySpend columns.
      brandId: brandData.brandId,
      name: brandData.name,
      domain: brandData.domain || '',
      status: brandData.status,
      createdAt: brandData.createdAt,
      updatedAt: brandData.updatedAt
    }));
    setBrands(mappedBrands);

    // scroll to top after fetching
    brandTableRef.current?.scrollTo({ top: 0 });
  };

  // fetches brands based on action taken
  const fetchBrands =
    (method: typeof getNextBrandBatch) =>
    async (keepLoading = false) => {
      const plans = Array.from(filteredPlans);
      // @TODO: Felix: Remove once ES brand index has been fixed in production
      if (plans.includes(PlanFilterOptions.Trial)) plans.push('freetrial', 'freeTrial');

      setLoading(true);
      const brandsBatch = await method({
        status: includeLockedBrands ? `${BrandStatus.Active},${BrandStatus.Locked}` : undefined,
        search: filterBrandName,
        plans: plans.join(','),
        spend: filteredSpend.join(',')
      });
      processBrands(brandsBatch);

      // fetch integrations for brands
      const brandIds = brandsBatch.map(b => b.brandId);

      await fetchIntegrationsAndAdAccountsForBrands(brandIds as string[], 'all');
      setLoading(keepLoading || false);
    };

  const fetchBrandsAndFavorites = async () => {
    await fetchBrands(getNextBrandBatch)(true);
    await fetchFavorites();
  };

  // fetches brands based on search
  const fetchSearchedBrands = async () => {
    fetchBrands(getBrands)();
  };

  const fetchPrevious = async () => {
    if (hasPrevious) fetchBrands(getPreviousBrandBatch)();
  };

  const fetchNext = async () => {
    if (hasNext) fetchBrands(getNextBrandBatch)();
  };
  /* ^ end fetch brands/favorite methods */

  /* v delete brand methods */
  const confirmDeleteBrand = (brandId: string) => {
    setBrandIdToDelete(brandId);
  };

  const deleteBrandAndRefresh = async () => {
    try {
      setLoading(true);
      setBrandStatusUpdating(true);
      await brandsApi.deleteBrand({ brandId: brandIdToDelete });
      const activeBrands = brands.filter(b => b.brandId !== brandIdToDelete);
      const activeFavorites = favorites.filter(f => f.brandId !== brandIdToDelete);
      setBrands(activeBrands);
      setFavorites(activeFavorites);
      setBrandIdToDelete('');
    } catch {
      /* TODO */
    }
    setLoading(false);
    setBrandStatusUpdating(false);
  };
  /* ^ end delete brand methods */

  /* v lock/unlock methods */
  const confirmLockBrand = (brandId: string) => {
    setBrandIdToLock(brandId);
  };

  const confirmUnlockBrand = (brandId: string) => {
    setBrandIdToUnlock(brandId);
  };

  const closeBrandLock = () => {
    setBrandIdToLock('');
  };

  const processAfterLockUnlockBrand = (action: 'lock' | 'unlock', brandId: string) => {
    const processedBrand = findBrandToUpdate(brandId);

    if (processedBrand) {
      const status = action === 'lock' ? BrandStatus.Locked : BrandStatus.Active;
      const updatedBrand = { ...processedBrand, status };

      const updatedBrands = brands.map(br => (br.brandId === brandId ? updatedBrand : br));

      setBrands(updatedBrands);

      const brandInFavorites = favorites.find(b => b.brandId === brandId);

      if (brandInFavorites) {
        const updatedFavorites = favorites.map(br => (br.brandId === brandId ? updatedBrand : br));

        setFavorites(updatedFavorites);
      }

      const message =
        action === 'lock'
          ? `Locked brand access for ${processedBrand?.name}`
          : `Unlocked brand access for ${processedBrand?.name}.`;

      showToaster({ message, variant: 'info' });
    }
  };

  const lockBrandAndRefresh = async (audienceOption: BrandLockAudienceOption) => {
    setLoading(true);
    setBrandStatusUpdating(true);
    try {
      await brandsApi.lockBrand({
        brandId: brandIdToLock,
        brandLockRequest: {
          audienceOption
        }
      });

      processAfterLockUnlockBrand('lock', brandIdToLock);
      setBrandIdToLock('');
    } catch {
      /* TODO */
    }
    setLoading(false);
    setBrandStatusUpdating(false);
  };

  const unlockBrandAndRefresh = async () => {
    setLoading(true);
    setBrandStatusUpdating(true);
    try {
      await brandsApi.unlockBrand({ brandId: brandIdToUnlock });

      processAfterLockUnlockBrand('unlock', brandIdToUnlock);
      setBrandIdToUnlock('');
    } catch {
      /* TODO */
    }
    setLoading(false);
    setBrandStatusUpdating(false);
  };
  /* ^ end lock/unlock methods */

  /* v join brand methods */
  const confirmJoinBrand = (brandId: string) => {
    setBrandIdToJoin(brandId);
  };

  const joinBrandAndRefresh = async () => {
    const email = user?.email;
    if (!email) {
      showToaster({
        message: 'Could not determine your account email. Please try again.',
        variant: 'error'
      });
      return;
    }

    const brandName = findBrandInAnyList(brandIdToJoin)?.name ?? 'brand';

    setLoading(true);
    setBrandStatusUpdating(true);
    try {
      await brandsApi.updateAccess({
        brandId: brandIdToJoin,
        updateAccessRequest: { email, roleId: 'admin' }
      });

      setBrandIdToJoin('');
      showToaster({
        message: (
          <>
            Added you to&nbsp;<strong>{brandName}</strong>.
          </>
        ),
        variant: 'success'
      });
      await fetchFavorites();
    } catch {
      showToaster({
        message: `Failed to add you to ${brandName}. Please try again.`,
        variant: 'error'
      });
    }
    setLoading(false);
    setBrandStatusUpdating(false);
  };
  /* ^ end join brand methods */

  const getIsAdAccountActive = ({ brandId, type }: AdAccountActiveProps) =>
    adAccounts.some(a => a.brandId === brandId && a.type === type) ||
    favoriteAdAccounts.some(a => a.brandId === brandId && a.type === type);

  brandListConfig.formatter = (row: Brand): BrandRow => {
    const isAdAccountActive = getIsAdAccountActive({
      brandId: row.brandId,
      type: IntegrationType.Facebook
    });

    const isTiktokAdAccountActive = getIsAdAccountActive({
      brandId: row.brandId,
      type: IntegrationType.TikTok
    });

    return {
      ...row,
      integrations: integrations[tab].filter(i => i.brandId === row.brandId),
      loading,
      isSelectedBrand: brand?.brandId === row.brandId,
      refreshFavorites: fetchFavorites,
      confirmDeleteBrand,
      confirmLockBrand,
      confirmUnlockBrand,
      confirmJoinBrand,
      canJoinBrand: tab === 'all',
      isAdAccountActive,
      isTiktokAdAccountActive,
      favorites
    };
  };

  /* v filter methods */
  const clearFilters = () => {
    setFilteredPlans([]);
    setFilteredSpend([]);
    setIncludeLockedBrands(false);
    setFiltersStateCounter(filterStateCounter + 1);
  };

  const onIncludeLockedBrands = (include: boolean) => {
    setSearchDirty(true);
    setIncludeLockedBrands(include);
  };

  const onFilterBrands = (value: string) =>
    debouncer(() => {
      const isDeleted = searchDirty && !value.length;
      const isNewSearch = value.length >= MAX_CHARS_FOR_SEARCH || !value.length;
      const doSearch = isDeleted || isNewSearch;

      if (doSearch) {
        setSearchDirty(true);
        setFilterBrandName(value);
      }
    });

  const onFilterPlans = (selectedPlans: string[]) =>
    debouncer(() => {
      setSearchDirty(true);
      setFilteredPlans(selectedPlans);
    });

  const onFilterSpend = (selectedItems: string[]) =>
    debouncer(() => {
      setSearchDirty(true);
      setFilteredSpend(selectedItems);
    });
  /* ^ end filter methods */

  /* v brand create methods */
  const onCloseCreateBrand = (reloadList?: boolean) => {
    setShowCreateBrand(false);
    if (reloadList) fetchBrandsAndFavorites();
  };
  /* ^ end brand create methods */

  // initial render
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    fetchBrandsAndFavorites();
  }, []);

  // fetches brands on search input change
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (isMounted.current) {
      if (searchDirty) fetchSearchedBrands();
    } else {
      isMounted.current = true;
    }
  }, [filterBrandName, filteredPlans, filteredSpend, includeLockedBrands]);

  const brandToUnlock = findBrandToUpdate(brandIdToUnlock);
  const brandToJoin = findBrandInAnyList(brandIdToJoin);
  const brandToLock = findBrandToUpdate(brandIdToLock);
  const brandToDelete = findBrandToUpdate(brandIdToDelete);

  useShowSpinner({ show: loading });

  return (
    <>
      <BrandListView
        onIncludeLockedBrands={onIncludeLockedBrands}
        includeLockedBrands={includeLockedBrands}
        hasSelectedFilters={hasSelectedFilters}
        filterStateCounter={filterStateCounter}
        clearFilters={clearFilters}
        loading={brandsLoading}
        brandTableRef={brandTableRef}
        brands={brands}
        favorites={favorites}
        config={brandListConfig}
        onFilterSpend={onFilterSpend}
        onFilterPlans={onFilterPlans}
        onFilterBrands={onFilterBrands}
        onCreateBrandClick={() => setShowCreateBrand(true)}
        onNext={hasNext ? fetchNext : undefined}
        onPrevious={hasPrevious ? fetchPrevious : undefined}
        onTabChange={setTab}
        selectedTab={tab}
        tabs={tabs}
      />
      <DeleteConfirmation
        isLoading={brandStatusUpdating}
        confirm={deleteBrandAndRefresh}
        open={!!brandIdToDelete}
        close={() => setBrandIdToDelete('')}
        brandName={brandToDelete?.name || ''}
      />
      <LockConfirmation
        isLoading={brandStatusUpdating}
        isAdAccountActive={getIsAdAccountActive({
          brandId: brandIdToLock,
          type: IntegrationType.Facebook
        })}
        onConfirm={lockBrandAndRefresh}
        onClose={closeBrandLock}
        open={!!brandIdToLock}
        brand={brandToLock}
      />
      <UnlockConfirmation
        confirm={unlockBrandAndRefresh}
        open={!!brandIdToUnlock}
        close={() => setBrandIdToUnlock('')}
        brandName={brandToUnlock?.name || ''}
      />
      <JoinConfirmation
        confirm={joinBrandAndRefresh}
        open={!!brandIdToJoin}
        close={() => setBrandIdToJoin('')}
        brandName={brandToJoin?.name || ''}
        isLoading={brandStatusUpdating}
      />
      <CreateBrandModal open={showCreateBrand} onClose={onCloseCreateBrand} />
    </>
  );
};

export default BrandList;
